import type { QueryClient } from "@tanstack/react-query";
import { MediaApi } from "@/openapi/generated";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  filterMediaItemsForOwner,
  pickLatestMedia,
  pickMediaItemUrl,
} from "@/lib/utils/media/fetch-media-asset";
import { getMediaAssetQueryKey } from "@/lib/utils/media/media-asset-query-key";

export type PrefetchTenantLogoResult = {
  url: string | null;
  mediaId: number | null;
  media: any | null;
};

export async function prefetchTenantLogo(
  queryClient: QueryClient,
  tenantId: number,
  accessToken?: string,
) {
  if (!accessToken) {
    // Don't throw - just skip prefetching if no token
    return queryClient;
  }

  const service = createAuthenticatedApi(MediaApi, accessToken);

  try {
    await queryClient.prefetchQuery({
      queryKey: getMediaAssetQueryKey(
        tenantId,
        EntryType.Tenant,
        tenantId,
        MediaGroup.Logo,
        "auth",
      ),
      queryFn: async (): Promise<PrefetchTenantLogoResult> => {
        try {
          const media = await service.apiMediaTypeIdGet({
            type: EntryType.Tenant,
            id: tenantId,
            xTenantID: Number(tenantId),
          });

          const items = filterMediaItemsForOwner(
            media || [],
            EntryType.Tenant,
            tenantId,
          );
          const logo = pickLatestMedia(
            items,
            (item) => item.mediaGroup === MediaGroup.Logo,
          );
          const url = pickMediaItemUrl(logo);

          return {
            url,
            mediaId: logo?.mediaId != null ? Number(logo.mediaId) : null,
            media: logo,
          };
        } catch {
          return {
            url: null,
            mediaId: null,
            media: null,
          };
        }
      },
    });
  } catch {
    // Prefetch is optional - UI falls back when logo is unavailable
  }

  return queryClient;
}

